import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Pencil, ArrowLeft, Eye } from 'lucide-react';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import userService from '../../services/UserService.js';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewUser = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await userService.getById(userId);
      setUser(response.data);
    } catch (err) {
      Alert.error('Failed to load user details');
      console.error(err);
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentUrl = () => {
    return userService.getDocumentAttachment(userId);
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <PageLayout title="User Details" icon={Users}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading user details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <PageLayout
        title="User Details"
        icon={Users}
        actions={
          <>
            {user.role_name !== 'Agent' && (
              <Button
                variant="secondary"
                icon={Pencil}
                onClick={() => navigate(`/users/edit/${userId}`)}
              >
                Edit User
              </Button>
            )}
            <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/users')}>
              Back to Users
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-primary rounded-card p-4 flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold text-secondary">{user.role_name}</h2>
            <StatusBadge status={user.status} />
          </div>

          <DetailCard title="Basic Information">
            <DetailRow label="Username" value={user.username || 'N/A'} />
            <DetailRow label="Full Name" value={user.name} />
            <DetailRow label="Email" value={user.email} />
            <DetailRow label="Date of Birth" value={formatDate(user.dob)} />
          </DetailCard>

          <DetailCard title="Contact Information">
            <DetailRow label="Contact Number" value={user.contact} />
            <DetailRow label="Branch" value={user.city} />
            <DetailRow label="Location/Address" value={user.location} />
          </DetailCard>

          <DetailCard title="Document Information">
            <DetailRow
              label="Document Type"
              value={
                user.document_type
                  ? user.document_type.replace(/_/g, ' ').toUpperCase()
                  : 'N/A'
              }
            />
            <DetailRow label="Document Number" value={user.document_number || 'N/A'} />
            <DetailRow
              label="Document Attachment"
              value={
                <Button variant="primary" size="sm" icon={Eye} onClick={openDocumentModal}>
                  View Document
                </Button>
              }
            />
          </DetailCard>

          {user.role_name === 'Driver' && (
            <DetailCard title="Driving License">
              <DetailRow
                label="Driving License Attachment"
                value={
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Eye}
                    onClick={() =>
                      window.open(userService.getDrivingLicenseAttachment(userId), '_blank')
                    }
                  >
                    View Driving License
                  </Button>
                }
              />
            </DetailCard>
          )}

          <DetailCard title="Role and Status">
            <DetailRow label="Role" value={user.role_name} />
            <DetailRow
              label="Monthly Salary"
              value={
                user.monthly_salary
                  ? `₹ ${parseFloat(user.monthly_salary).toLocaleString('en-IN')}`
                  : 'N/A'
              }
            />
            <DetailRow
              label="Shipment Commission %"
              value={
                user.shipment_commission != null ? `${user.shipment_commission}%` : 'N/A'
              }
            />
            <DetailRow label="Status" value={<StatusBadge status={user.status} />} />
          </DetailCard>

          <DetailCard title="System Information">
            <DetailRow label="User ID" value={user.user_id} />
            <DetailRow label="Created By" value={user.created_by || 'N/A'} />
            <DetailRow label="Created At" value={formatDate(user.created_at)} />
            <DetailRow label="Last Updated" value={formatDate(user.updated_at)} />
          </DetailCard>
        </div>
      </PageLayout>

      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeDocumentModal}
        documentUrl={getDocumentUrl()}
        documentType={user.document_type}
      />
    </>
  );
};

export default ViewUser;
